import React from 'react'
import { Link } from '@inertiajs/react'
import { BedDouble, ArrowRight } from 'lucide-react'
import DashboardCard from '@/components/dashboard/DashboardCard'
import type { PricingStatus } from '@/types/shared'
import { PLANS_NOT_PAIRED_LABEL, twinNotPricedLabel, unpairedPlansLabel } from '@/lib/combined-inventory'

/**
 * Property Setup — the always-available equivalent of the onboarding review's
 * read-only inventory. When the calendar says a room has no active rate plans,
 * this is where the operator comes to see the whole property structure as the
 * PMS has it. Display-only: every field (incl. pricingStatus) is backend-derived
 * and rendered verbatim; the deep work happens on the Rooms Setup page.
 */

export interface PropertySetupRoom {
  id: number
  name: string
  totalUnits: number
  maxOccupancy: number
  activePlanCount: number
  totalPlanCount: number
  leadPlanName: string | null
  /** Backend-derived (RoomType#pricing_status) — rendered, never re-derived. */
  pricingStatus: PricingStatus
  /** Non-lead plans with no offset yet (RoomType#unpriced_rate_plans) — not priced. */
  unpricedPlanCount?: number
  hasLeadPlan: boolean
  isLinked: boolean
  /**
   * Combined inventory: set on a Same room only. Its plans copy its twin's, so an unpriced plan is
   * paired in the room mapping (`mappingHref`, null when this person cannot change it). `name` is the
   * twin's, named when the twin has no price ('twin_not_priced').
   */
  sameAs?: { name?: string; label: string; mappingHref: string | null } | null
}

export interface PropertySetupData {
  roomTypes: PropertySetupRoom[]
}

interface PropertySetupCardProps {
  propertySetup: PropertySetupData
  /** The property's backend-served canonical path. */
  propertyBasePath: string
  /** From the shared syncStatus prop (last structure sync). Null = never synced. */
  lastStructureSync: string | null
}

// Same child-link glyph the Rooms Setup tree uses for indented linked rooms.
const LINK_GLYPH = 'M19 15l-6 6-1.42-1.42L15.17 16H4V4h2v10h9.17l-3.59-3.58L13 9l6 6z'

function timeAgo(isoString: string): string {
  const diffMins = Math.floor((Date.now() - new Date(isoString).getTime()) / 60000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return `on ${new Date(isoString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
}

function plansSummary(room: PropertySetupRoom): string {
  if (room.totalPlanCount === 0) return 'No rate plans in your PMS'
  if (room.pricingStatus === 'no_active_plans') return 'No active plans in your PMS'
  const plans =
    room.activePlanCount < room.totalPlanCount
      ? `${room.activePlanCount} of ${room.totalPlanCount} plans active`
      : `${room.totalPlanCount} ${room.totalPlanCount === 1 ? 'plan' : 'plans'} active`
  return room.leadPlanName ? `${plans} · lead: ${room.leadPlanName}` : plans
}

function RoomRow({ room }: { room: PropertySetupRoom }) {
  const notPriced = room.pricingStatus === 'no_active_plans'
  const missingBaseRate = room.pricingStatus === 'missing_base_rate'
  const noLeadNudge = room.pricingStatus === 'priced' && !room.hasLeadPlan
  const unpricedPlans = room.unpricedPlanCount ?? 0

  return (
    <div
      className="flex items-start gap-3 px-5 py-2.5 transition-colors hover:bg-muted/20"
      style={{ paddingLeft: room.isLinked ? 44 : undefined }}
      data-pricing-status={room.pricingStatus}
    >
      {room.isLinked ? (
        <svg
          viewBox="0 0 24 24"
          className="mt-1 h-4 w-4 shrink-0 text-primary/70"
          fill="currentColor"
          aria-hidden
        >
          <path d={LINK_GLYPH} />
        </svg>
      ) : (
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary/8">
          <BedDouble className="h-3 w-3 text-primary" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="truncate text-sm font-medium text-foreground">{room.name}</span>
          {notPriced && (
            <span
              className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700"
              title="No active rate plans in your PMS — this room isn't priced. Informational: hotels legitimately block rooms this way."
            >
              Not priced
            </span>
          )}
          {missingBaseRate && (
            <span
              className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"
              title="This room has active plans but no base rate yet, so no prices are calculated for it. Set one under Manage rooms."
            >
              No base rate
            </span>
          )}
          {noLeadNudge && (
            <span
              className="inline-flex shrink-0 items-center rounded-full border border-dashed border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
              title="No lead rate plan chosen — other plans have nothing to follow. Pick one under Manage rooms."
            >
              No lead plan set
            </span>
          )}
          {room.sameAs && (
            <span className="inline-flex shrink-0 items-center rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-primary">
              {room.sameAs.label}
            </span>
          )}
          {room.sameAs && room.pricingStatus === 'twin_not_priced' && (
            <span
              className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"
              title="A Same room sells at the price of the room it is the same room as. That room has no price yet, so this one is not priced either."
            >
              {twinNotPricedLabel(room.sameAs.name ?? 'the room it copies')}
            </span>
          )}
          {room.sameAs && room.pricingStatus === 'plans_not_paired' && (
            <span
              className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"
              title="A Same room copies the prices of the room it is the same room as, plan by plan. None of its rate plans is paired yet, so it is not priced."
            >
              {PLANS_NOT_PAIRED_LABEL}
            </span>
          )}
          {unpricedPlans > 0 && room.sameAs && (
            room.sameAs.mappingHref ? (
              <Link
                href={room.sameAs.mappingHref}
                title="A rate plan without a partner isn't priced. Pair it in the room mapping."
                className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 transition-colors hover:bg-amber-200"
              >
                {unpairedPlansLabel(unpricedPlans)}
              </Link>
            ) : (
              <span
                className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                title="A rate plan without a partner isn't priced. It is paired in the room mapping, in business settings."
              >
                {unpairedPlansLabel(unpricedPlans)}
              </span>
            )
          )}
          {unpricedPlans > 0 && !room.sameAs && (
            <span
              className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"
              title="These plans have no offset from the lead yet, so the engine doesn't price them. Set how each follows the lead under Manage rooms."
            >
              {unpricedPlans} {unpricedPlans === 1 ? 'plan' : 'plans'} not priced
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {room.totalUnits} {room.totalUnits === 1 ? 'unit' : 'units'} · sleeps{' '}
          {room.maxOccupancy} · {plansSummary(room)}
        </p>
      </div>
    </div>
  )
}

export default function PropertySetupCard({
  propertySetup,
  propertyBasePath,
  lastStructureSync,
}: PropertySetupCardProps) {
  const rooms = propertySetup.roomTypes

  return (
    <DashboardCard
      title="Property Setup"
      description={
        lastStructureSync
          ? `Synced from your PMS ${timeAgo(lastStructureSync)}`
          : 'Not synced from your PMS yet'
      }
      icon={<BedDouble className="h-4 w-4 shrink-0 text-muted-foreground" />}
      help={{
        content:
          'Your rooms and rate plans exactly as they came across from your PMS. If a room shows as not priced or missing a base rate, this is why the calendar has gaps for it. Structure changes made in your PMS appear here after the next sync.',
        learnMore: '/guides/base-rates',
      }}
      action={
        <Link
          href={`${propertyBasePath}/pricing-rules/rooms-setup`}
          className="inline-flex shrink-0 items-center gap-1 pt-0.5 text-xs font-medium text-primary transition-colors hover:text-primary/80"
        >
          Manage rooms
          <ArrowRight className="h-3 w-3" />
        </Link>
      }
      padded={false}
    >
      {rooms.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <BedDouble className="mx-auto h-8 w-8 text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium text-foreground">No rooms yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Your rooms appear here after the first sync from your PMS.
          </p>
        </div>
      ) : (
        // Review surface, not a workbench — rows stay tight and the list scrolls
        // instead of letting a large property dominate the dashboard.
        <div className="max-h-80 divide-y divide-border/30 overflow-y-auto py-1">
          {rooms.map((room) => (
            <RoomRow key={room.id} room={room} />
          ))}
        </div>
      )}
    </DashboardCard>
  )
}
